"use client";

import { observeDOM } from "@aichat/aichat-lib";
import { DATE_FORMAT_BASIC, DATE_FORMAT_VERBOSE } from "@aichat/constants/date";
import { UserConversationRoleEnum } from "@aichat/types/enums/index";
import { MessageContent, MessageEmit } from "@aichat/types/socket/widget.types";
import { HandleSendComponentParams } from "@aichat/types/widget";
import { cn } from "@aichat/ui/lib/utils";
import { format, isSameDay, parseISO } from "date-fns";
import { ArrowDown } from "lucide-react";
import { useState } from "react";
import { Button } from "../button";
import { Skeleton } from "../skeleton";
import ChatBubble__V2 from "./_components/chat-bubble";
import { MessageContainerProps } from "./_components/message-container";
import SystemMessageComponent from "./_components/systemMessage";

type ChatListProps = {
  messages: MessageContent[];
  type?: "portal-chat" | "widget-chat";
  conversationLabelName?: string;
  isStreaming?: boolean;
  // eslint-disable-next-line no-unused-vars
  handleSendComponent?: (params: HandleSendComponentParams) => void;
  handleSendForm?: (
    // eslint-disable-next-line no-unused-vars
    data: MessageEmit["message"]["form_response"],
  ) => void;
  className?: string;
  chatListRef?: React.RefObject<HTMLDivElement>;
  showDigitalHuman?: boolean;
  scrollToBottomArrowHidden?: boolean;
  widgetConfig: MessageContainerProps["widgetConfig"];
};

function ChatList__V2({
  messages,
  conversationLabelName,
  type = "widget-chat",
  isStreaming,
  handleSendComponent,
  handleSendForm,
  chatListRef,
  className,
  showDigitalHuman,
  widgetConfig,
  scrollToBottomArrowHidden = false,
}: ChatListProps) {
  const [isScrolling, setIsScrolling] = useState(false);

  const scrollToBottom = () => {
    if (chatListRef?.current) {
      chatListRef.current.scrollBy({
        top: chatListRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  function onScroll(e: React.UIEvent<HTMLDivElement, UIEvent>) {
    const currentScrollHeight = e.currentTarget.scrollHeight;
    const currentScrollTop = e.currentTarget.scrollTop;

    const diff = currentScrollHeight - currentScrollTop;
    const isAtBottom = Math.abs(diff - e.currentTarget.clientHeight) < 2;
    setIsScrolling(!isAtBottom);
    const videos = document.querySelectorAll(".aichat-video-preview");
    videos.forEach((video) => observeDOM().observe(video));
    const youtubes = document.querySelectorAll(".aichat-youtube-preview");
    youtubes.forEach((iframe) => observeDOM("youtube").observe(iframe));
  }

  let dateDiff = "";
  return (
    <div
      ref={chatListRef}
      onScroll={onScroll}
      className={cn("flex flex-col overflow-auto p-4 relative", className)}
      style={
        showDigitalHuman
          ? {
              maskImage: "linear-gradient(to top, black 60%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to top, black 60%, transparent 100%)",
              maskComposite: "exclude",
              clipPath: "polygon(0 0, 100% 0, 100% 99%, 0 100%)",
            }
          : undefined
      }
    >
      {messages.length <= 0 ? (
        <MessagesLoading />
      ) : (
        messages.map((message, index) => {
          const isFirstSameRoleBubble =
            index === 0 || messages[index - 1]?.role !== message.role;
          const isLastSameRoleBubble =
            index === messages.length - 1 ||
            messages[index + 1]?.role !== message.role;
          const isMiddleSameRoleBubble =
            !isFirstSameRoleBubble && !isLastSameRoleBubble;

          const shouldAnimate =
            index === 0 &&
            message.role === UserConversationRoleEnum.BOT &&
            isStreaming;

          const parsedMsgDate = parseISO(message.createdAt);
          const msgDate = format(parsedMsgDate, DATE_FORMAT_BASIC);

          const showDateHeaderFirst = index === 0;
          const showDateHeader = dateDiff && dateDiff !== msgDate;
          dateDiff = format(parsedMsgDate, DATE_FORMAT_BASIC);

          const displayDate = isSameDay(new Date(), parsedMsgDate)
            ? "Today"
            : format(parsedMsgDate, DATE_FORMAT_VERBOSE);

          return (
            <div
              key={message.id}
              className={cn(
                message.role === UserConversationRoleEnum.PENDING
                  ? "opacity-60"
                  : undefined,
                "flex flex-col gap-4",
              )}
            >
              {(showDateHeader || showDateHeaderFirst) && (
                <SystemMessageComponent message={displayDate} />
              )}

              <ChatBubble__V2
                widgetConfig={widgetConfig}
                hideAvatarInTextBubble={showDigitalHuman}
                conversationLabelName={conversationLabelName}
                type={type}
                index={index}
                role={message.role}
                createdAt={message.createdAt}
                message={message}
                isFirstSameRoleBubble={isFirstSameRoleBubble}
                isLastSameRoleBubble={isLastSameRoleBubble}
                isMiddleSameRoleBubble={isMiddleSameRoleBubble}
                shouldAnimate={shouldAnimate}
                handleSendComponent={handleSendComponent}
                handleSendForm={handleSendForm}
              />
            </div>
          );
        })
      )}
      {!scrollToBottomArrowHidden && messages.length > 0 && (
        <div
          className={cn(
            "fixed bottom-[120px] z-40 left-0 w-full flex items-center justify-center transition-all duration-300 ease-in-out pointer-events-none",
            "opacity-100 translate-y-0",
            isScrolling
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-full",
          )}
        >
          <Button
            variant="outline"
            onClick={scrollToBottom}
            className="w-10 h-10 border border-border rounded-full mx-auto pointer-events-auto"
          >
            <ArrowDown />
          </Button>
        </div>
      )}
    </div>
  );
}

export default ChatList__V2;

function MessagesLoading() {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: 2 }).map((_, index) => (
        <div className="w-[70%]" key={`message-skeleton-${index}`}>
          <Skeleton className="h-[72px]" />
        </div>
      ))}
    </div>
  );
}
